import { useState } from "react";
import { Copy, KeyRound, Settings2, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDemo } from "@/store/DemoStore";
import { Avatar, Card, Empty, PageHeader, SearchInput, btn, normalizePhone, isPhoneKey, normalizePhoneOnBlur } from "@/components/common";

const field = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 disabled:bg-slate-50 disabled:text-slate-400 sm:text-sm";
const label = "mb-1.5 block text-xs font-medium text-slate-500";

function ManageDialog({ client, onClose }) {
  const { agents, updateClient, reassignClient, resetClientPassword, deleteClient } = useDemo();
  const [f, setF] = useState({ name: client.name, phone: client.phone || "", address: client.address || "", agentId: client.agentId });
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const activeAgents = agents.filter((a) => a.active);
  const set = (k) => (e) => setF({ ...f, [k]: isPhoneKey(k) ? normalizePhone(e.target.value) : e.target.value });

  const save = async () => {
    setBusy(true);
    await updateClient(client.id, { name: f.name, phone: f.phone, address: f.address });
    if (f.agentId !== client.agentId) await reassignClient(client.id, f.agentId);
    setBusy(false);
    toast.success("Client updated");
    onClose();
  };
  const reset = async () => {
    const r = await resetClientPassword(client.id);
    if (r) setCreds(r);
  };
  const remove = async () => {
    const r = await deleteClient(client.id);
    if (r) onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[calc(var(--app-h,100dvh)-2rem)] w-[calc(100vw-1.5rem)] max-w-xl overflow-y-auto rounded-2xl p-5 sm:p-7" data-testid="user-manage-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-navy-900">Manage {client.name}</DialogTitle>
          <DialogDescription>Registered MyTrip account details. Reassign the advisor or manage the account below.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="sm:col-span-2"><span className={label}>Name</span><input className={field} value={f.name} onChange={set("name")} data-testid="user-name-input" /></label>
          <label><span className={label}>Email (sign in)</span><input className={field} value={client.email} disabled data-testid="user-email-input" /></label>
          <label><span className={label}>Phone</span><input className={field} value={f.phone} onChange={set("phone")} onBlur={normalizePhoneOnBlur((n) => setF((p) => ({ ...p, phone: n })))} data-testid="user-phone-input" /></label>
          <label><span className={label}>Member since</span><input className={field} value={client.since || "—"} disabled data-testid="user-since-input" /></label>
          <label><span className={label}>Assigned advisor</span>
            <select className={field} value={f.agentId} onChange={set("agentId")} data-testid="user-advisor-select">
              {activeAgents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
          <label className="sm:col-span-2"><span className={label}>Mailing address</span><input className={field} value={f.address} onChange={set("address")} placeholder="Street, city, state, ZIP" data-testid="user-address-input" /></label>
          <label className="sm:col-span-2"><span className={label}>Emergency contact</span><input className={field} value="" disabled placeholder="Coming soon" data-testid="user-emergency-input" /></label>
        </div>

        <div className="mt-2 flex justify-end gap-2">
          <button onClick={onClose} className={btn.secondary}>Cancel</button>
          <button onClick={save} disabled={busy} className={btn.primary} data-testid="user-save-btn">Save Changes</button>
        </div>

        <div className="mt-5 rounded-xl border border-slate-200 p-4" data-testid="user-actions">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Account actions</p>
          {creds ? (
            <div className="space-y-3" data-testid="user-reset-creds">
              <p className="text-sm text-slate-600">New temporary password for {creds.email}. Shown once — share it with the client.</p>
              <pre className="whitespace-pre-wrap break-all rounded-lg bg-slate-50 p-3 font-mono text-sm text-navy-900">{`Email: ${creds.email}\nTemporary password: ${creds.tempPassword}`}</pre>
              <button onClick={() => { navigator.clipboard?.writeText(`Email: ${creds.email}\nTemporary password: ${creds.tempPassword}`); toast.success("Copied"); }} className={btn.secondary} data-testid="user-reset-copy"><Copy className="h-4 w-4" />Copy</button>
            </div>
          ) : confirmDelete ? (
            <div className="space-y-3" data-testid="user-delete-confirm">
              <p className="text-sm text-red-600">This permanently deletes {client.name}'s account, trips and messages. This can't be undone.</p>
              <div className="flex gap-2">
                <button onClick={() => setConfirmDelete(false)} className={btn.secondary}>Cancel</button>
                <button onClick={remove} className={`${btn.primary} bg-red-600 hover:bg-red-700`} data-testid="user-delete-confirm-btn"><Trash2 className="h-4 w-4" />Delete Permanently</button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button onClick={reset} className={btn.secondary} data-testid="user-reset-btn"><KeyRound className="h-4 w-4" />Reset Password</button>
              <button onClick={() => setConfirmDelete(true)} className={`${btn.secondary} text-red-600 hover:bg-red-50`} data-testid="user-delete-btn"><Trash2 className="h-4 w-4" />Delete Account</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const { clients, agentName } = useDemo();
  const [q, setQ] = useState("");
  const [manageId, setManageId] = useState(null);
  const s = q.toLowerCase();
  const list = clients.filter((c) => c.status === "active").filter((c) => [c.name, c.email].join(" ").toLowerCase().includes(s));
  const managing = clients.find((c) => c.id === manageId);

  return (
    <div className="max-w-5xl" data-testid="admin-users-page">
      <PageHeader title="Users" subtitle={`${list.length} active portal ${list.length === 1 ? "account" : "accounts"} · MyTrip profiles created`} />
      <div className="mb-5">
        <SearchInput value={q} onChange={setQ} placeholder="Search by client name or email" testId="users-search-input" className="md:max-w-md" />
      </div>
      <Card className="overflow-hidden">
        <div className="hidden grid-cols-[2fr_2fr_1.5fr_auto] gap-4 border-b border-slate-100 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400 md:grid">
          <span>Client name</span><span>Email</span><span>Advisor</span><span>Manage</span>
        </div>
        <ul className="divide-y divide-slate-100" data-testid="users-list">
          {list.map((c) => (
            <li key={c.id} className="grid grid-cols-[1fr_auto] items-center gap-4 px-4 py-4 sm:px-6 md:grid-cols-[2fr_2fr_1.5fr_auto]" data-testid={`user-row-${c.id}`}>
              <div className="flex min-w-0 items-center gap-3">
                <Avatar name={c.name} />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-navy-900">{c.name}</p>
                  <p className="truncate text-xs text-slate-500 md:hidden">{c.email}</p>
                </div>
              </div>
              <p className="hidden truncate text-sm text-navy-900 md:block">{c.email}</p>
              <p className="hidden truncate text-sm text-slate-600 md:block">{agentName(c.agentId) || "—"}</p>
              <button onClick={() => setManageId(c.id)} className={btn.secondary} data-testid={`user-manage-${c.id}`}><Settings2 className="h-4 w-4" />Manage</button>
            </li>
          ))}
        </ul>
        {!list.length && <Empty icon={Users} title="No active accounts" text="Clients appear here once they create their MyTrip account." />}
      </Card>
      {managing && <ManageDialog key={managing.id} client={managing} onClose={() => setManageId(null)} />}
    </div>
  );
}
